import { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Schedule as ScheduleType } from "@/types/branch";
import { useResponsive } from "@/hooks";

const weekDays = [
  { label: "Lunes", id: "mon", abbr: "Lu" },
  { label: "Martes", id: "tue", abbr: "Ma" },
  { label: "Miércoles", id: "wed", abbr: "Mi" },
  { label: "Jueves", id: "thu", abbr: "Ju" },
  { label: "Viernes", id: "fri", abbr: "Vi" },
  { label: "Sábado", id: "sat", abbr: "Sá" },
  { label: "Domingo", id: "sun", abbr: "Do" },
];

const initializeSchedule = (schedule?: ScheduleType | null): ScheduleType => {
  return Object.fromEntries(
    weekDays.map(({ id }) => [
      id,
      {
        closed: schedule?.[id]?.closed ?? true,
        openHours: schedule?.[id]?.openHours ?? [],
      },
    ])
  ) as ScheduleType;
};

interface Props {
  title?: string;
  subtitle?: string;
  schedule?: ScheduleType | null;
  onChange: (newSchedule: ScheduleType) => void;
}

const Schedule = ({ title, subtitle, schedule, onChange }: Props) => {
  const { isMobile } = useResponsive();
  const [values, setValues] = useState<ScheduleType>(() =>
    initializeSchedule(schedule)
  );

  const handleToggleClosed = (id: string, closed: boolean) => {
    setValues((prevValues) => {
      const updatedSchedule = {
        ...prevValues,
        [id]: {
          ...prevValues[id],
          closed,
          openHours: closed
            ? []
            : prevValues[id].openHours.length > 0
              ? prevValues[id].openHours
              : [{ start: "08:00", end: "20:00" }],
        },
      };

      onChange(updatedSchedule);
      return updatedSchedule;
    });
  };

  const handleTimeChange = (
    id: string,
    timeIndex: number,
    field: "start" | "end",
    value: string
  ) => {
    setValues((prevValues) => {
      const updatedHours = prevValues[id].openHours.map((time: any, index: any) =>
        index === timeIndex ? { ...time, [field]: value } : time
      );

      const updatedSchedule = {
        ...prevValues,
        [id]: {
          ...prevValues[id],
          openHours: updatedHours,
        },
      };

      onChange(updatedSchedule);
      return updatedSchedule;
    });
  };

  useEffect(() => {
    setValues(initializeSchedule(schedule));
  }, [schedule]);

  return (
    <Stack spacing={2}>
      <Stack>
        {title && <Typography variant="h3">{title}</Typography>}
        {subtitle && <Typography variant="body1">{subtitle}</Typography>}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ButtonGroup fullWidth size="small">
            {weekDays.map(({ label, id, abbr }) => {
              const day = values[id];
              const isSelected = !day?.closed;

              return (
                <Button
                  key={id}
                  variant={isSelected ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handleToggleClosed(id, !day?.closed)}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontWeight: isSelected ? 700 : 400,
                  }}
                >
                  {isMobile ? abbr : label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {weekDays.map(({ label, id }) => {
            const day = values[id];

            return (
              <Stack
                spacing={2}
                key={id}
                direction="row"
                alignContent="center"
                justifyContent="space-between"
                width="100%"
              >
                {!day?.closed && (
                  <>
                    <Typography variant="h4" sx={{ width: "100%" }}>
                      {label}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="flex-end"
                      width="100%"
                      paddingY={1}
                    >
                      {day?.openHours?.map((range: any, timeIndex: any) => (
                        <Stack
                          key={timeIndex}
                          direction="row"
                          spacing={2}
                          sx={{ paddingTop: 0, paddingBottom: 0 }}
                        >
                          <TextField
                            type="time"
                            size="small"
                            margin="normal"
                            label="Abre a"
                            value={range.start}
                            onChange={(e) =>
                              handleTimeChange(
                                id,
                                timeIndex,
                                "start",
                                e.target.value
                              )
                            }
                          />

                          <TextField
                            type="time"
                            size="small"
                            margin="normal"
                            label="Cierra a"
                            value={range.end}
                            onChange={(e) =>
                              handleTimeChange(
                                id,
                                timeIndex,
                                "end",
                                e.target.value
                              )
                            }
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            );
          })}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default Schedule;